/**
 * OnboardingPage Component
 * Individual slide component for onboarding carousel
 */

import React from 'react';
import { OnboardingPage as OnboardingPageType } from './types';

interface OnboardingPageProps {
  page: OnboardingPageType;
  onNext: () => void;
  onSecondary?: () => void; // For "Log In" button on final page
  isActive: boolean;
}

/**
 * Responsive Image Component
 * Automatically generates @1x/@2x srcset for optimized WebP images
 */
const ResponsiveImage = ({ 
  src, 
  alt, 
  className 
}: { 
  src: string; 
  alt: string; 
  className: string;
}) => {
  // Generate @2x path by replacing @1x with @2x
  const src2x = src.replace('@1x', '@2x');
  
  return (
    <picture>
      <source 
        srcSet={`${src} 1x, ${src2x} 2x`}
        type="image/webp"
      />
      <img
        src={src}
        alt={alt}
        className={className}
      />
    </picture>
  );
};

export const OnboardingPage: React.FC<OnboardingPageProps> = ({
  page,
  onNext,
  onSecondary,
  isActive
}) => {
  const hasSecondaryButton = !!page.secondaryButtonText && !!onSecondary;
  const [isRecentlyActivated, setIsRecentlyActivated] = React.useState(false);
  
  // Prevent auto-clicks when page just became active
  React.useEffect(() => {
    if (isActive) {
      setIsRecentlyActivated(true);
      const timer = setTimeout(() => {
        setIsRecentlyActivated(false);
      }, 300); // 300ms cooldown after page becomes active
      return () => clearTimeout(timer);
    }
  }, [isActive]);
  
  const handlePrimaryClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (isRecentlyActivated) {
      return;
    }
    onNext();
  };
  
  const handleSecondaryClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (isRecentlyActivated) {
      return;
    }
    if (onSecondary) {
      onSecondary();
    }
  };
  
  return (
    <div
      className={`absolute inset-0 transition-opacity duration-300 ${
        isActive ? 'opacity-100 z-10' : 'opacity-0 z-0 pointer-events-none'
      }`}
    >
      {/* Mobile Layout */}
      <div className="md:hidden h-full flex flex-col">
        {/* Illustration Area - Top 60% */}
        <div className="relative h-[60%] overflow-hidden bg-white">
          {page.id === 1 ? (
            <>
              {/* Welcome hero icon - responsive width (behind scene) */}
              {page.images.mobile[1] && (
                <ResponsiveImage
                  src={page.images.mobile[1]}
                  alt="Welcome hero"
                  className="absolute left-1/2 -translate-x-1/2 top-[60px] w-[155%] h-auto max-w-[725px] object-contain z-0"
                />
              )}
              {/* Sigiriya scene - scaled to full width (in front) */}
              {page.images.mobile[0] && (
                <ResponsiveImage
                  src={page.images.mobile[0]}
                  alt="Sigiriya scene"
                  className="absolute left-0 bottom-0 w-full h-auto object-cover z-10"
                />
              )}
            </>
          ) : page.id === 2 ? (
            <>
              {/* Scene at bottom */}
              {page.images.mobile[0] && (
                <ResponsiveImage
                  src={page.images.mobile[0]}
                  alt="Scene"
                  className="absolute left-0 bottom-0 w-full h-[221px] object-cover"
                />
              )}
              {/* Large chat bubble - top right */}
              {page.images.mobile[1] && (
                <ResponsiveImage
                  src={page.images.mobile[1]}
                  alt="Chat icon large"
                  className="absolute right-[20px] top-[90px] w-[208px] h-[173px] object-contain"
                />
              )}
              {/* Small chat bubble - bottom left */}
              {page.images.mobile[2] && (
                <ResponsiveImage
                  src={page.images.mobile[2]}
                  alt="Chat icon small"
                  className="absolute left-[54px] top-[180px] w-[113px] h-[93px] object-contain"
                />
              )}
            </>
          ) : page.id === 3 ? (
            <>
              {/* Temple scene at bottom - full width */}
              {page.images.mobile[0] && (
                <ResponsiveImage
                  src={page.images.mobile[0]}
                  alt="Temple scene"
                  className="absolute left-0 bottom-0 w-full h-auto object-cover"
                />
              )}
              {/* Search icon at top */}
              {page.images.mobile[1] && (
                <ResponsiveImage
                  src={page.images.mobile[1]}
                  alt="Search icon"
                  className="absolute left-1/2 -translate-x-1/2 top-[100px] w-[80%] h-auto max-w-[400px] object-contain"
                />
              )}
            </>
          ) : page.id === 4 ? (
            <>
              {/* Ruins scene - scaled to full width */}
              {page.images.mobile[0] && (
                <ResponsiveImage
                  src={page.images.mobile[0]}
                  alt="Ruins scene"
                  className="absolute left-0 top-[265px] w-full h-auto object-cover"
                />
              )}
              {/* Route icon at top - responsive width */}
              {page.images.mobile[1] && (
                <ResponsiveImage
                  src={page.images.mobile[1]}
                  alt="Route icon"
                  className="absolute left-1/2 -translate-x-1/2 top-[80px] w-[83%] h-auto max-w-[400px] object-contain"
                />
              )}
            </>
          ) : page.id === 5 ? (
            <>
              {/* Fishermen scene - scaled to full width */}
              {page.images.mobile[0] && (
                <ResponsiveImage
                  src={page.images.mobile[0]}
                  alt="Fishermen scene"
                  className="absolute left-0 top-[211px] w-full h-auto object-cover"
                />
              )}
              {/* Tuk-tuk icon at top - slightly left of center */}
              {page.images.mobile[1] && (
                <ResponsiveImage
                  src={page.images.mobile[1]}
                  alt="Tuk-tuk"
                  className="absolute left-[45%] -translate-x-1/2 top-[65px] w-[60%] h-auto max-w-[350px] object-contain"
                />
              )}
            </>
          ) : (
            page.images.mobile.map((img: string, idx: number) => (
              <img
                key={idx}
                src={img}
                alt={`${page.title} illustration ${idx + 1}`}
                className="absolute inset-0 w-full h-full object-cover"
                style={{
                  zIndex: page.images.mobile.length - idx
                }}
              />
            ))
          )}
        </div>

        {/* Divider */}
        <div className="h-px bg-[#e5e7eb]" />

        {/* Content Area - Bottom 40% */}
        <div className="flex-1 bg-white px-8 pt-12 pb-8 flex flex-col justify-between">
          <div className="flex-1 flex flex-col items-center justify-center">
            {/* Heading */}
            <h1
              className="text-[28px] font-bold leading-[33.6px] tracking-[-0.56px] text-[#1a1a1a] text-center mb-4"
              style={{ fontFamily: 'Inter, sans-serif' }}
            >
              {page.title}
            </h1>

            {/* Description */}
            <p
              className="text-[16px] font-normal leading-[24px] text-[#6b7280] text-center max-w-[316px]"
              style={{ fontFamily: 'Inter, sans-serif' }}
            >
              {page.description}
            </p>
          </div>

          {/* Buttons */}
          <div className="space-y-3">
            <button
              type="button"
              onClick={handlePrimaryClick}
              tabIndex={isActive ? 0 : -1}
              className="w-full h-[52px] bg-gradient-to-r from-[#14b8a6] to-[#663399] hover:from-[#0f9d8e] hover:to-[#552288] active:from-[#0d8a7a] active:to-[#4F2D32] text-white font-semibold text-[16px] leading-[24px] rounded-[12px] transition-colors duration-200"
              style={{
                fontFamily: 'Inter, sans-serif',
                boxShadow: '0px 4px 12px 0px rgba(102, 51, 153, 0.25)'
              }}
            >
              {page.buttonText}
            </button>
            
            {hasSecondaryButton && (
              <button
                type="button"
                onClick={handleSecondaryClick}
                tabIndex={isActive ? 0 : -1}
                className="w-full h-[52px] bg-white hover:bg-gray-50 text-gray-900 font-semibold text-[16px] leading-[24px] rounded-[12px] transition-colors duration-200 border-2 border-gray-200"
                style={{
                  fontFamily: 'Inter, sans-serif'
                }}
              >
                {page.secondaryButtonText}
              </button>
            )}
            
            {/* Legal Links - Only show for pages 1-5 */}
            {page.id <= 5 && (
              <div className="flex items-center justify-center gap-2 pt-2 text-[10px] text-gray-500">
                <a
                  href="/privacy"
                  className="text-gray-500 hover:text-gray-700 underline transition-colors"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                >
                  Privacy
                </a>
                <span className="text-gray-300">•</span>
                <a
                  href="/terms"
                  className="text-gray-500 hover:text-gray-700 underline transition-colors"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                >
                  Terms
                </a>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Desktop Layout - Split Screen */}
      <div className="hidden md:flex h-full relative">
        {/* Left Panel - Background only (719px wide) */}
        <div className="relative w-[719px] bg-[#fafafa] overflow-hidden">
          {/* Background illustration */}
          {page.images.desktop[0] && (
            <ResponsiveImage
              src={page.images.desktop[0]}
              alt={`${page.title} background`}
              className={page.id === 1 ? "absolute left-0 top-[249px] w-[720px] h-[473px] object-cover" : page.id === 2 ? "absolute left-0 top-[286px] w-[720px] h-[405px] object-cover" : page.id === 3 ? "absolute left-0 top-[263px] w-[719px] h-[395px] object-cover" : page.id === 4 ? "absolute left-0 top-[345px] w-[724px] h-[296px] object-cover" : page.id === 5 ? "absolute left-0 top-[272px] w-[719px] h-[473px] object-cover" : page.id === 6 ? "absolute left-0 top-0 w-[719px] h-[909px] object-cover" : "absolute left-0 top-[249px] w-[720px] h-[473px] object-cover"}
            />
          )}
        </div>

        {/* Vertical Divider */}
        <div className="w-px bg-[#e5e7eb]" />

        {/* Right Panel - Content */}
        <div className="flex-1 bg-white flex items-end justify-center pb-16 px-16 relative">
          <div className="max-w-[541px] text-center relative">
            {/* Page 1: Phone mockup - centered above the title, scales with container */}
            {page.id === 1 && page.images.desktop[1] && (
              <div className="absolute w-[125%] h-auto left-1/2 -translate-x-1/2 bottom-[calc(100%+15px)] pointer-events-none z-0">
                <ResponsiveImage
                  src={page.images.desktop[1]}
                  alt={`${page.title} phone mockup`}
                  className="w-full h-auto object-contain"
                />
              </div>
            )}
            
            {/* Page 2: Chat bubble icon */}
            {page.id === 2 && page.images.desktop[1] && (
              <div className="absolute w-[90%] h-auto left-1/2 -translate-x-1/2 bottom-[calc(100%+15px)] pointer-events-none z-0">
                <ResponsiveImage
                  src={page.images.desktop[1]}
                  alt="Chat icon"
                  className="w-full h-auto object-contain"
                />
              </div>
            )}
            
            {/* Page 3: Search icon */}
            {page.id === 3 && page.images.desktop[1] && (
              <div className="absolute w-[110%] h-auto left-1/2 -translate-x-1/2 bottom-[calc(100%+15px)] pointer-events-none z-0">
                <ResponsiveImage
                  src={page.images.desktop[1]}
                  alt="Search icon"
                  className="w-full h-auto object-contain"
                />
              </div>
            )}
            
            {/* Page 4: Route/navigation icon */}
            {page.id === 4 && page.images.desktop[1] && (
              <div className="absolute w-[110%] h-auto left-1/2 -translate-x-1/2 bottom-[calc(100%+15px)] pointer-events-none z-0">
                <ResponsiveImage
                  src={page.images.desktop[1]}
                  alt="Route icon"
                  className="w-full h-auto object-contain"
                />
              </div>
            )}
            
            {/* Page 5: Tuk-tuk icon */}
            {page.id === 5 && page.images.desktop[1] && (
              <div className="absolute w-[110%] h-auto left-[30%] -translate-x-1/2 bottom-[calc(100%+15px)] pointer-events-none z-0">
                <ResponsiveImage
                  src={page.images.desktop[1]}
                  alt="Tuk-tuk"
                  className="w-full h-auto object-contain"
                />
              </div>
            )}
            
            {/* Page 6: Seashell decorative element */}
            {page.id === 6 && page.images.desktop[2] && (
              <div className="absolute w-[118%] h-auto -top-40 left-1/2 -translate-x-1/2 pointer-events-none z-0">
                <img
                  src={page.images.desktop[2]}
                  alt="Shell decoration"
                  className="w-full h-auto object-contain"
                />
              </div>
            )}
            
            {/* Content with higher z-index to stay on top */}
            <div className="relative z-10">
            {/* Heading */}
            <h1
              className="text-[32px] font-bold leading-[40px] tracking-[-0.56px] text-[#1a1a1a] mb-5"
              style={{ fontFamily: 'Inter, sans-serif' }}
            >
              {page.id === 1 ? (
                <>
                  Welcome to <span className="whitespace-nowrap">'hey SriLanka'</span>
                </>
              ) : (
                page.title
              )}
            </h1>

            {/* Description */}
            <p
              className="text-[20px] font-normal leading-[26px] text-[#6b7280] mb-10"
              style={{ fontFamily: 'Inter, sans-serif' }}
            >
              {page.description}
            </p>

            {/* Buttons */}
            <div className="space-y-3 flex flex-col items-center mb-8">
              <button
                type="button"
                onClick={handlePrimaryClick}
                tabIndex={isActive ? 0 : -1}
                className="w-[327px] h-[52px] bg-gradient-to-r from-[#14b8a6] to-[#663399] hover:from-[#0f9d8e] hover:to-[#552288] active:from-[#0d8a7a] active:to-[#4F2D32] text-white font-semibold text-[16px] leading-[24px] rounded-[12px] transition-colors duration-200"
                style={{
                  fontFamily: 'Inter, sans-serif',
                  boxShadow: '0px 4px 12px 0px rgba(102, 51, 153, 0.25)'
                }}
              >
                {page.buttonText}
              </button>
              
              {hasSecondaryButton && (
                <button
                  type="button"
                  onClick={handleSecondaryClick}
                  tabIndex={isActive ? 0 : -1}
                  className="w-[327px] h-[52px] bg-white hover:bg-gray-50 text-gray-900 font-semibold text-[16px] leading-[24px] rounded-[12px] transition-colors duration-200 border-2 border-gray-200"
                  style={{
                    fontFamily: 'Inter, sans-serif'
                  }}
                >
                  {page.secondaryButtonText}
                </button>
              )}
              
              {/* Legal Links - Only show for pages 1-5 */}
              {page.id <= 5 && (
                <div className="flex items-center justify-center gap-2 pt-1 text-[11px] text-gray-500">
                  <a
                    href="/privacy"
                    className="text-gray-500 hover:text-gray-700 underline transition-colors"
                    style={{ fontFamily: 'Inter, sans-serif' }}
                  >
                    Privacy
                  </a>
                  <span className="text-gray-300">•</span>
                  <a
                    href="/terms"
                    className="text-gray-500 hover:text-gray-700 underline transition-colors"
                    style={{ fontFamily: 'Inter, sans-serif' }}
                  >
                    Terms
                  </a>
                </div>
              )}
            </div>

            {/* Progress Dots */}
            <div className="flex items-center justify-center gap-[8px]">
              {[0, 1, 2, 3, 4, 5].map((index) => (
                <div
                  key={index}
                  className="w-[8px] h-[8px] rounded-full"
                  style={{
                    backgroundColor: page.id === index + 1 ? '#663399' : '#e5e7eb'
                  }}
                />
              ))}
            </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

